import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { loadRecaptchaScript, getRecaptchaToken } from "@/lib/recaptcha";


const inputCls =
  "w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 outline-none transition-colors focus:border-brand/60 focus:bg-white/[0.06]";
const labelCls = "mb-1.5 block font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-400";
const errCls = "mt-1 text-xs text-red-400";

const Field = ({ label, error, children, htmlFor }) => (
  <div>
    <label htmlFor={htmlFor} className={labelCls}>
      {label}
    </label>
    {children}
    {error && <p className={errCls} data-testid={`${htmlFor}-error`}>{error}</p>}
  </div>
);

const Honeypot = ({ value, onChange }) => (
  <input
    type="text"
    name="website"
    value={value}
    onChange={onChange}
    tabIndex={-1}
    autoComplete="off"
    aria-hidden="true"
    className="hidden"
    data-testid="field-honeypot"
  />
);

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function TeamProposalForm() {
  const empty = {
    first_name: "",
    last_name: "",
    business_name: "",
    email: "",
    phone: "",
    country: "Australia",
    role_required: "",
    department: "",
    num_people: "",
    engagement_type: "Full-time",
    start_date: "",
    budget_range: "",
    message: "",
    referral_source: "",
    website: "",
  };
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    loadRecaptchaScript();
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const errs = {};
    if (!form.first_name.trim()) errs.first_name = "Required";
    if (!form.last_name.trim()) errs.last_name = "Required";
    if (!form.business_name.trim()) errs.business_name = "Required";
    if (!EMAIL_RE.test(form.email)) errs.email = "Enter a valid business email";
    if (!form.phone.trim()) errs.phone = "Required";
    if (!form.role_required.trim()) errs.role_required = "Required";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
await axios.post(
"https://formspree.io/f/xdeaeyrv",
{
formType: "Team Proposal",
...form
}
);
      setDone(true);
      toast.success("Request received. We'll be in touch shortly.");
    } catch (err) {
      toast.error("Something went wrong sending your request. Please try again or email info@synergyos.com.au.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="team-form-success" className="rounded-2xl border border-brand/30 bg-brand-soft p-10 text-center">
        <CheckCircle2 className="mx-auto h-12 w-12 text-brand" aria-hidden="true" />
        <h3 className="mt-4 font-display text-2xl font-bold text-white">Request received.</h3>
        <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-mist">
          Thanks {form.first_name}. We'll review your requirements and come back with the most suitable options for{" "}
          {form.business_name}. No obligation.
        </p>
        <button
          data-testid="team-form-reset"
          onClick={() => {
            setForm(empty);
            setDone(false);
          }}
          className="mt-6 rounded-full border border-white/15 px-6 py-2.5 text-sm font-semibold text-slate-200 transition-colors hover:border-brand/50"
        >
          Submit another request
        </button>
      </div>
    );
  }

  return (
    <form data-testid="team-proposal-form" onSubmit={submit} noValidate className="space-y-5">
      <Honeypot value={form.website} onChange={set("website")} />
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="First Name *" error={errors.first_name} htmlFor="first_name">
          <input id="first_name" data-testid="field-first-name" className={inputCls} value={form.first_name} onChange={set("first_name")} autoComplete="given-name" />
        </Field>
        <Field label="Last Name *" error={errors.last_name} htmlFor="last_name">
          <input id="last_name" data-testid="field-last-name" className={inputCls} value={form.last_name} onChange={set("last_name")} autoComplete="family-name" />
        </Field>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Business Name *" error={errors.business_name} htmlFor="business_name">
          <input id="business_name" data-testid="field-business-name" className={inputCls} value={form.business_name} onChange={set("business_name")} autoComplete="organization" />
        </Field>
        <Field label="Business Email *" error={errors.email} htmlFor="email">
          <input id="email" type="email" data-testid="field-email" className={inputCls} value={form.email} onChange={set("email")} autoComplete="email" />
        </Field>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Phone *" error={errors.phone} htmlFor="phone">
          <input id="phone" type="tel" data-testid="field-phone" className={inputCls} value={form.phone} onChange={set("phone")} autoComplete="tel" />
        </Field>
        <Field label="Country" htmlFor="country">
          <select id="country" data-testid="field-country" className={inputCls} value={form.country} onChange={set("country")}>
            {["Australia", "United States", "Canada", "United Kingdom", "New Zealand", "Other"].map((c) => (
              <option key={c} value={c} className="bg-ink-2">{c}</option>
            ))}
          </select>
        </Field>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Role Required *" error={errors.role_required} htmlFor="role_required">
          <input id="role_required" data-testid="field-role-required" className={inputCls} placeholder="e.g. Business Analyst, Customer Support" value={form.role_required} onChange={set("role_required")} />
        </Field>
        <Field label="Department" htmlFor="department">
          <select id="department" data-testid="field-department" className={inputCls} value={form.department} onChange={set("department")}>
            <option value="" className="bg-ink-2">Select…</option>
            {["Business Operations", "Customer Operations", "Sales & Lead Generation", "Social Media & Marketing", "Finance & Accounting", "IT Services", "Professional Services", "Other"].map((d) => (
              <option key={d} value={d} className="bg-ink-2">{d}</option>
            ))}
          </select>
        </Field>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Number of People" htmlFor="num_people">
          <select id="num_people" data-testid="field-num-people" className={inputCls} value={form.num_people} onChange={set("num_people")}>
            <option value="" className="bg-ink-2">Select…</option>
            {["Just 1", "2–3", "4–6", "7–10", "10+"].map((n) => (
              <option key={n} value={n} className="bg-ink-2">{n}</option>
            ))}
          </select>
        </Field>
        <Field label="Engagement" htmlFor="engagement_type">
          <div className="flex gap-3 pt-1" role="radiogroup" aria-label="Engagement type">
            {["Full-time", "Part-time"].map((t) => (
              <button
                key={t}
                type="button"
                data-testid={`field-engagement-${t.toLowerCase().replace("-", "")}`}
                onClick={() => setForm((f) => ({ ...f, engagement_type: t }))}
                aria-pressed={form.engagement_type === t}
                className={`flex-1 rounded-xl border px-4 py-3 text-sm font-semibold transition-colors ${
                  form.engagement_type === t
                    ? "border-brand/60 bg-brand-soft text-brand"
                    : "border-white/10 bg-white/[0.04] text-slate-400 hover:border-white/20"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </Field>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Required Start Date" htmlFor="start_date">
          <input id="start_date" type="date" data-testid="field-start-date" className={inputCls} value={form.start_date} onChange={set("start_date")} />
        </Field>
        <Field label="Budget Range" htmlFor="budget_range">
          <select id="budget_range" data-testid="field-budget-range" className={inputCls} value={form.budget_range} onChange={set("budget_range")}>
            <option value="" className="bg-ink-2">Select…</option>
            {["Under $1,500 / month", "$1,500 – $3,000 / month", "$3,000 – $6,000 / month", "$6,000+ / month", "Not sure yet"].map((b) => (
              <option key={b} value={b} className="bg-ink-2">{b}</option>
            ))}
          </select>
        </Field>
      </div>
      <Field label="Tell us about the role" htmlFor="message">
        <textarea id="message" rows={4} data-testid="field-message" className={inputCls} placeholder="Responsibilities, tools, hours, outcomes you're looking for…" value={form.message} onChange={set("message")} />
      </Field>
      <Field label="How did you hear about Synergy Outsourcing?" htmlFor="referral_source">
        <select id="referral_source" data-testid="field-referral-source" className={inputCls} value={form.referral_source} onChange={set("referral_source")}>
          <option value="" className="bg-ink-2">Select…</option>
          {["Google Search", "Referral", "LinkedIn", "Event or community", "Other"].map((r) => (
            <option key={r} value={r} className="bg-ink-2">{r}</option>
          ))}
        </select>
      </Field>
      <button
        type="submit"
        data-testid="team-form-submit"
        disabled={submitting}
        className="group flex w-full items-center justify-center gap-2 rounded-full bg-brand px-7 py-4 text-sm font-semibold text-ink transition-colors hover:bg-brand-bright disabled:opacity-60"
      >
        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
        Request My Team Proposal
        {!submitting && <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />}
      </button>
      <p className="text-center text-xs leading-relaxed text-slate-500">
        No obligation. We'll review your requirements and come back with the most suitable options.
      </p>
    </form>
  );
}

export function ContactForm() {
  const empty = { name: "", email: "", company: "", phone: "", message: "", website: "" };
  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    loadRecaptchaScript();
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Required";
    if (!EMAIL_RE.test(form.email)) errs.email = "Enter a valid email";
    if (!form.phone.trim()) errs.phone = "Required";
    if (!form.message.trim()) errs.message = "Required";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const recaptcha_token = await getRecaptchaToken("contact");
     await axios.post(
"https://formspree.io/f/xdeaeyrv",
{
formType: "Contact Form",
...form
}
);
      setDone(true);
      toast.success("Message sent. We'll be in touch shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please try again or email info@synergyos.com.au.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="contact-form-success" className="rounded-2xl border border-brand/30 bg-brand-soft p-10 text-center">
        <CheckCircle2 className="mx-auto h-12 w-12 text-brand" aria-hidden="true" />
        <h3 className="mt-4 font-display text-2xl font-bold text-white">Message received.</h3>
        <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-mist">
          Thanks {form.name}. We typically respond within one business day.
        </p>
      </div>
    );
  }

  return (
    <form data-testid="contact-form" onSubmit={submit} noValidate className="space-y-5">
      <Honeypot value={form.website} onChange={set("website")} />
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Name *" error={errors.name} htmlFor="c_name">
          <input id="c_name" data-testid="contact-field-name" className={inputCls} value={form.name} onChange={set("name")} autoComplete="name" />
        </Field>
        <Field label="Email *" error={errors.email} htmlFor="c_email">
          <input id="c_email" type="email" data-testid="contact-field-email" className={inputCls} value={form.email} onChange={set("email")} autoComplete="email" />
        </Field>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Company" htmlFor="c_company">
          <input id="c_company" data-testid="contact-field-company" className={inputCls} value={form.company} onChange={set("company")} autoComplete="organization" />
        </Field>
        <Field label="Phone *" error={errors.phone} htmlFor="c_phone">
          <input id="c_phone" type="tel" data-testid="contact-field-phone" className={inputCls} value={form.phone} onChange={set("phone")} autoComplete="tel" />
        </Field>
      </div>
      <Field label="Message *" error={errors.message} htmlFor="c_message">
        <textarea id="c_message" rows={5} data-testid="contact-field-message" className={inputCls} placeholder="Tell us about the role or team you're considering…" value={form.message} onChange={set("message")} />
      </Field>
      <button
        type="submit"
        data-testid="contact-form-submit"
        disabled={submitting}
        className="group flex w-full items-center justify-center gap-2 rounded-full bg-brand px-7 py-4 text-sm font-semibold text-ink transition-colors hover:bg-brand-bright disabled:opacity-60"
      >
        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
        Start a Conversation
        {!submitting && <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />}
      </button>
    </form>
  );
}
